import { useTranslation } from 'react-i18next';
import { listNodiComuni } from '@/api/endpoints';
import type { ElencoNodiComuni } from '@/api/endpoints';
import { useAsync } from '@/lib/hooks';
import type { AsyncState } from '@/lib/hooks';

/**
 * L'elenco dei comuni, UNO per tutta l'applicazione.
 * ---------------------------------------------------------------------------
 * Tre punti lo consumano — il dropdown «Comuni» dell'header, il picker
 * dell'hero e la mappa a costellazione — e sulla home sono montati tutti e
 * tre insieme. Senza condivisione sarebbero tre richieste identiche.
 *
 * Perché una PROMISE memoizzata e non un contesto React:
 *  · un contesto obbligherebbe a montare un provider sopra l'app per un dato
 *    che serve a tre componenti, e a passarlo comunque a chi sta fuori
 *    dall'albero (i test montano `<Header />` da solo);
 *  · la promise si condivide anche fra componenti montati in momenti
 *    diversi, che è il caso vero: l'header c'è su ogni pagina, la mappa solo
 *    sulla home;
 *  · è il pattern che il progetto usa già per le fonti di attribuzione
 *    (`listFonti`/`resetFontiCache` in `api/endpoints.ts`), e la decisione 20
 *    dice di non introdurre librerie di stato.
 *
 * ⚠️ La richiesta condivisa NON riceve un `AbortSignal`. È deliberato: il
 * segnale sarebbe quello del PRIMO componente che chiede: se si smontasse
 * per primo (l'header naviga, la mappa resta) annullerebbe la richiesta
 * anche per gli altri. `useAsync` scarta comunque il risultato che arriva
 * dopo lo smontaggio, quindi non c'è nessun aggiornamento su un componente
 * morto — si paga solo una risposta che nessuno legge.
 *
 * La cache è per LINGUA: `listNodiComuni` negozia `Accept-Language` come
 * ogni altra lista, e servire l'elenco italiano dopo un cambio lingua
 * sarebbe un dato sbagliato messo in cache.
 */

const cache = new Map<string, Promise<ElencoNodiComuni>>();

/** Chiave di cache: il codice a due lettere, come fa `getPagina`. */
function chiaveLingua(lingua: string | undefined): string {
  return lingua?.slice(0, 2).toLowerCase() || 'it';
}

/**
 * L'elenco condiviso. Fuori da React, così lo può usare anche codice che non
 * è un componente (e i test, senza montare nulla).
 *
 * Un fallimento NON resta in cache: l'errore di rete di un momento
 * condannerebbe la sessione intera a un menu vuoto.
 */
export function elencoComuni(lingua: string): Promise<ElencoNodiComuni> {
  const chiave = chiaveLingua(lingua);
  const inCorso = cache.get(chiave);
  if (inCorso) return inCorso;

  const richiesta = listNodiComuni({ language: chiave }).catch(
    (errore: unknown) => {
      cache.delete(chiave);
      throw errore;
    },
  );
  cache.set(chiave, richiesta);
  return richiesta;
}

/** Azzera la memoizzazione. Serve ai test, non all'applicazione. */
export function resetElencoComuni(): void {
  cache.clear();
}

/**
 * Lo stato dell'elenco per un componente. Quattro stati come ovunque, ma
 * `notFound` qui non capita: l'endpoint restituisce sempre una collezione,
 * e una collezione vuota è un `success` con zero righe — che è precisamente
 * lo stato reale finché la base dati non ha comuni.
 */
export function useComuni(): AsyncState<ElencoNodiComuni> {
  const { i18n } = useTranslation();
  return useAsync<ElencoNodiComuni>(
    () => elencoComuni(i18n.language),
    [i18n.language],
  );
}
